import { useState } from "react";
import { Transition } from "@headlessui/react";
import { useNavigate } from "react-router-dom";
import { useMount, useUpdateEffect } from "react-use";
import { useMutation, useQueryClient } from "react-query";
import moment from "moment";
import FindOutMethod from "../../components/user/FindOutMethod";
import FindOutInfo from "../../components/user/FindOutInfo";
import FindOutPassword from "../../components/user/FindOutPassword";
import {
  confirmVerifyForFindOut,
  sendVerifyForFindOut,
  updateUserInfo,
} from "../../api";
import Navbar from "../../components/common/Navbar";

const FindOutPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [type, setType] = useState(null);
  const [target, setTarget] = useState(null);
  const [method, setMethod] = useState(null);
  const [step, setStep] = useState(0);
  const [title, setTitle] = useState("아이디 찾기 및 비밀번호 재설정");
  const [description, setDesc] = useState("");
  const [name, setName] = useState("");
  const [id, setId] = useState(null);
  const [result, setResult] = useState(null);
  const [sendResult, setSendResult] = useState(null);
  const [confirmResult, setConfirmResult] = useState(null);

  const { mutate: sendMutation } = useMutation({
    mutationFn: sendVerifyForFindOut,
    onSettled: (res) => {
      if (res && res.result) {
        const { expired_at } = res.data;
        setId(res.data.id);
        const t = new moment(expired_at).diff(new moment());
        setSendResult({ result: true, t, m: "인증 번호를 입력하세요." });
      } else {
        setSendResult({ result: false, m: res.error });
      }
      queryClient.invalidateQueries({ queryKey: ["find/verify/send"] });
    },
  });

  const { mutate: confirmMutation } = useMutation({
    mutationFn: confirmVerifyForFindOut,
    onSettled: (res) => {
      if (res && res.result) {
        handleInfo(res.data.userId);
        setConfirmResult({
          result: true,
          m:
            type === "userId"
              ? "정보 확인에 성공하였습니다."
              : "인증에 성공하였습니다.",
        });
      } else {
        setConfirmResult({ result: false, m: res.error });
      }
      queryClient.invalidateQueries({ queryKey: ["find/verify/confirm"] });
    },
  });

  const { mutate: updateMutation } = useMutation({
    mutationFn: updateUserInfo,
    onSettled: (res) => {
      if (res && res.result) {
        return setStep(5);
      }
      setConfirmResult({ result: false, m: res.error });
      queryClient.invalidateQueries({ queryKey: ["find/update"] });
    },
  });

  const handleSend = (params) => {
    let obj = {};
    if (type === "userId") {
      obj = { target, name, ...params };
    } else {
      obj = { userId: name, ...params };
    }
    sendMutation(obj);
  };

  const handleConfirm = (params) => {
    confirmMutation(id ? { ...params, id } : { ...params, target, name });
  };

  const handleType = (e) => {
    if (e.target.id === "login") return navigate("/");
    setType(e.target.id);
    if (e.target.id === "userId") setStep(1);
    if (e.target.id === "password") setStep(2);
  };

  const handleTarget = (e) => {
    setTarget(e.target.id);
    if (type === "userId") setStep(2);
  };

  const handleMethod = (e) => {
    setMethod(e.target.id);
    setStep(3);
  };

  const handleInfo = (v) => {
    if (type === "userId") {
      setResult(v);
      setStep(5);
    } else {
      setStep(4);
    }
  };

  const handleUpdate = (password) => {
    updateMutation({ id, password });
  };

  const handlePrev = () => {
    if (step === 0) {
      navigate("/login");
    } else if (type === "password" && step === 2) {
      setStep(0);
    } else {
      setStep(step - 1);
    }
  };

  useUpdateEffect(() => {
    switch (step) {
      case 1:
        setTitle("아이디 찾기");
        setDesc("회원 유형을 선택하세요.");
        break;
      case 2:
        if (type === "userId") {
          setTitle("아이디 찾기");
          if (target === "teacher") {
            setDesc("이름을 입력하세요.");
          } else {
            setDesc("학생 이름을 입력하세요.");
          }
        } else {
          setTitle("비밀번호 재설정");
          setDesc("");
        }
        break;
      case 3:
        let t = title;
        if (method === "tel") {
          setTitle(`휴대전화번호 인증으로 ${t}`);
          setDesc("휴대전화번호를 입력하세요.");
        } else if (method === "email") {
          setTitle(`이메일 인증으로 ${t}`);
          setDesc("이메일을 입력하세요.");
        } else {
          setTitle(`정보 인증으로 ${t}`);
          setDesc("가입시 입력한 정보를 입력하세요.");
        }
        break;
      case 4:
        setTitle(`${name}계정의 비밀번호 재설정`);
        setDesc("새로운 비밀번호를 입력하세요.");
        break;
      case 5:
        if (type === "userId") {
          setTitle(`${name}님의 아이디는`);
          setDesc("");
        } else {
          setTitle(`${name}계정의 비밀번호 재설정이 완료되었습니다.`);
          setDesc("");
        }
        break;
      default:
        setTitle("아이디 찾기 및 비밀번호 재설정");
        setDesc("");
        break;
    }
  }, [step]);

  useMount(() => {
    setType(null);
    setTarget(null);
    setMethod(null);
    setStep(0);
    setTitle("아이디 찾기 및 비밀번호 재설정");
    setDesc("");
    setName("");
    setResult(null);
    setSendResult(null);
  });

  return (
    <>
      <Navbar />
      <div className="hero min-h-screen bg-base-200 place-items-stretch">
        <div className="hero-content flex-col lg:flex-row-reverse m-0 lg:max-w-none">
          <div className="card flex-shrink-0 w-full max-w-sm shadow-2xl bg-base-100">
            <div className="card-body">
              <div className="inline-flex items-start gap-4">
                {step < 4 && (
                  <button onClick={handlePrev} className="mx-[-1rem]">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 18 24"
                      strokeWidth={2}
                      stroke="currentColor"
                      className="w-6 h-7"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15.75 19.5L8.25 12l7.5-7.5"
                      />
                    </svg>
                  </button>
                )}
                <header className="flex-1 w-full text-2xl font-bold">
                  {title}
                </header>
              </div>
              <div className="flex flex-col overflow-hidden w-auto gap-2">
                {description && (
                  <label className="label text-sm m-0 font-bold text-zinc-500">
                    {description}
                  </label>
                )}
                <Transition
                  show={step === 0}
                  enter="transition-all duration-150"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="transition-all duration-200"
                  leaveTo="-translate-x-full"
                >
                  <div className="flex flex-col gap-2 mt-2">
                    <button
                      id="userId"
                      className="btn btn-primary"
                      onClick={handleType}
                    >
                      아이디 찾기
                    </button>
                    <button
                      id="password"
                      className="btn btn-primary"
                      onClick={handleType}
                    >
                      비밀번호 재설정
                    </button>
                  </div>
                </Transition>
                <Transition
                  show={step === 1}
                  enter="transition-all duration-150 delay-150"
                  enterFrom="translate-y-full"
                  enterTo="translate-y-0"
                  leave="transition-all duration-200"
                  leaveTo="-translate-x-full"
                >
                  <div className="form-control">
                    <div className="flex flex-col gap-2">
                      {[
                        [
                          "student",
                          "bg-yellow-400 hover:bg-yellow-300",
                          "학생",
                        ],
                        ["parent", "bg-green-500 hover:bg-green-400", "학부모"],
                        ["teacher", "bg-blue-400 hover:bg-blue-300", "강사"],
                      ].map(([t, c, l]) => (
                        <button
                          id={t}
                          key={t}
                          className={`btn border-0 text-black ${c}`}
                          onClick={handleTarget}
                        >
                          {l}
                        </button>
                      ))}
                    </div>
                  </div>
                </Transition>
                <Transition
                  show={step === 2}
                  enter="transition-all duration-150 delay-150"
                  enterFrom="translate-y-full"
                  enterTo="translate-y-0"
                  leave="transition-all duration-200"
                >
                  <FindOutMethod
                    {...{ type, name, setName, onChange: handleMethod }}
                  />
                </Transition>
                <Transition
                  show={step === 3 && !!method}
                  enter="transition-all duration-150 delay-150"
                  enterFrom="translate-y-full"
                  enterTo="translate-y-0"
                  leave="transition-all duration-200"
                >
                  <FindOutInfo
                    {...{
                      method,
                      sendResult,
                      confirmResult,
                      onSend: handleSend,
                      onConfirm: handleConfirm,
                    }}
                  />
                </Transition>
                <Transition
                  show={step === 4}
                  enter="transition-all duration-150 delay-150"
                  enterFrom="translate-y-full"
                  enterTo="translate-y-0"
                  leave="transition-all duration-200"
                >
                  <FindOutPassword
                    {...{ confirmResult, onUpdate: handleUpdate }}
                  />
                </Transition>
                <Transition
                  show={step === 5}
                  enter="transition-all duration-150 delay-150"
                  enterFrom="translate-y-full"
                  enterTo="translate-y-0"
                  leave="transition-all duration-200"
                >
                  <div className="form-control gap-2">
                    <div className="inline-flex gap-2 items-baseline">
                      <header className="font-extrabold text-info text-xl">
                        {result}
                      </header>
                      {type === "userId" && (
                        <header className="flex-1 w-full text-2xl font-bold mb-4">
                          입니다.
                        </header>
                      )}
                    </div>
                    {method === "info" && (
                      <label className="label text-sm m-0 text-zinc-500">
                        정보 인증으로 아이디를 찾는 경우에는 아이디의
                        일부(3글자)만 제공하며 문자열의 길이는 무작위로
                        반환합니다.
                      </label>
                    )}
                    <button
                      id="login"
                      className="btn btn-accent"
                      onClick={handleType}
                    >
                      로그인 페이지로 이동
                    </button>
                    {type === "userId" && (
                      <button
                        id="password"
                        className="btn btn-primary"
                        onClick={handleType}
                      >
                        비밀번호 재설정
                      </button>
                    )}
                  </div>
                </Transition>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FindOutPage;
